import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { validateEmail, parseDocumentList } from '../utils/helpers';

const EDUCATION_OPTIONS = [
  'Secondary School (A2)',
  'Certificate',
  'Diploma',
  "Bachelor's Degree",
  "Master's Degree",
  'PhD / Doctorate',
  'Other'
];

const PHONE_CODES = [
  { code: '+250', label: '🇷🇼 Rwanda +250' },
  { code: '+256', label: '🇺🇬 Uganda +256' },
  { code: '+254', label: '🇰🇪 Kenya +254' },
  { code: '+255', label: '🇹🇿 Tanzania +255' },
  { code: '+257', label: '🇧🇮 Burundi +257' },
  { code: '+243', label: '🇨🇩 DR Congo +243' },
  { code: '+242', label: '🇨🇬 Congo +242' },
  { code: '+1', label: '🇺🇸 USA / Canada +1' },
  { code: '+44', label: '🇬🇧 United Kingdom +44' },
  { code: '+33', label: '🇫🇷 France +33' },
  { code: '+49', label: '🇩🇪 Germany +49' },
  { code: '+39', label: '🇮🇹 Italy +39' },
  { code: '+34', label: '🇪🇸 Spain +34' },
  { code: '+31', label: '🇳🇱 Netherlands +31' },
  { code: '+32', label: '🇧🇪 Belgium +32' },
  { code: '+41', label: '🇨🇭 Switzerland +41' },
  { code: '+46', label: '🇸🇪 Sweden +46' },
  { code: '+47', label: '🇳🇴 Norway +47' },
  { code: '+45', label: '🇩🇰 Denmark +45' },
  { code: '+358', label: '🇫🇮 Finland +358' },
  { code: '+61', label: '🇦🇺 Australia +61' },
  { code: '+64', label: '🇳🇿 New Zealand +64' },
  { code: '+65', label: '🇸🇬 Singapore +65' },
  { code: '+60', label: '🇲🇾 Malaysia +60' },
  { code: '+91', label: '🇮🇳 India +91' },
  { code: '+86', label: '🇨🇳 China +86' },
  { code: '+81', label: '🇯🇵 Japan +81' },
  { code: '+82', label: '🇰🇷 South Korea +82' },
  { code: '+971', label: '🇦🇪 UAE +971' },
  { code: '+966', label: '🇸🇦 Saudi Arabia +966' },
  { code: '+974', label: '🇶🇦 Qatar +974' },
  { code: '+20', label: '🇪🇬 Egypt +20' },
  { code: '+212', label: '🇲🇦 Morocco +212' },
  { code: '+234', label: '🇳🇬 Nigeria +234' },
  { code: '+233', label: '🇬🇭 Ghana +233' },
  { code: '+27', label: '🇿🇦 South Africa +27' },
  { code: '+351', label: '🇵🇹 Portugal +351' },
  { code: '+48', label: '🇵🇱 Poland +48' },
  { code: '+380', label: '🇺🇦 Ukraine +380' },
  { code: '+7', label: '🇷🇺 Russia +7' }
];

const CareerApply = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [positions, setPositions] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneCode: '+250',
    positionId: location.state?.positionId || '',
    educationLevel: '',
    yearsOfExperience: ''
  });
  const [cv, setCv] = useState(null);
  const [coverLetter, setCoverLetter] = useState(null);
  const [documents, setDocuments] = useState({});
  const [dataConsent, setDataConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeVerifying, setCodeVerifying] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');

  useEffect(() => {
    fetch('/api/careers/positions')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setPositions(data);
      })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'positionId') setDocuments({});
  };

  const handleDocChange = (doc, file) => {
    setDocuments(prev => ({ ...prev, [doc]: file }));
    setError('');
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, phone: digits }));
    setError('');
    setFieldErrors(prev => ({ ...prev, phone: '' }));
  };

  const handlePhoneCodeChange = (e) => {
    setFormData(prev => ({ ...prev, phoneCode: e.target.value }));
    setError('');
    setFieldErrors(prev => ({ ...prev, phone: '' }));
  };

  const handleEmailVerification = async (e) => {
    e.preventDefault();
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    setEmailChecking(true);
    setError('');
    setVerificationMessage('');

    try {
      await axios.post('/api/careers/send-verification-code', { email: formData.email });
      setCodeSent(true);
      setVerificationMessage('✓ A verification code has been sent to your email. Please check your inbox.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error sending verification code');
      setCodeSent(false);
    } finally {
      setEmailChecking(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!verificationCode) {
      setError('Please enter the verification code');
      return;
    }

    setCodeVerifying(true);
    setError('');
    setVerificationMessage('');

    try {
      await axios.post('/api/careers/verify-code', { email: formData.email, code: verificationCode });
      setEmailVerified(true);
      setVerificationMessage('✓ Email verified successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setCodeVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (!validateEmail(formData.email)) {
      setFieldErrors({ email: 'Invalid email format' });
      setLoading(false);
      return;
    }
    const fullPhone = (formData.phoneCode || '+250').replace(/\D/g, '') + formData.phone;
    if (!/^\d{8,15}$/.test(fullPhone)) {
      setFieldErrors({ phone: 'Enter a valid international phone number (country code + number)' });
      setLoading(false);
      return;
    }
    if (!formData.positionId) {
      setFieldErrors({ positionId: 'Please select a position' });
      setLoading(false);
      return;
    }
    const selected = positions.find(p => String(p.id) === String(formData.positionId));
    if (selected?.deadline && new Date(selected.deadline) < new Date()) {
      setError('The application deadline for this position has passed');
      setLoading(false);
      return;
    }
    if (!cv) {
      setError('Please upload your CV/Resume (PDF, DOC, or DOCX, under 5MB)');
      setLoading(false);
      return;
    }
    if (cv.size > 5 * 1024 * 1024) {
      setError('CV file exceeds the 5MB limit');
      setLoading(false);
      return;
    }
    if (!coverLetter) {
      setError('Please upload your Cover Letter (PDF, under 5MB)');
      setLoading(false);
      return;
    }
    if (coverLetter.size > 5 * 1024 * 1024) {
      setError('Cover letter file exceeds the 5MB limit');
      setLoading(false);
      return;
    }
    if (coverLetter.type !== 'application/pdf' && !/\.pdf$/i.test(coverLetter.name)) {
      setError('Cover letter must be a PDF file');
      setLoading(false);
      return;
    }
    const requiredDocs = parseDocumentList(selected?.requiredDocuments);
    for (const doc of requiredDocs) {
      const file = documents[doc];
      if (!file) {
        setError(`Please upload the required document: ${doc}`);
        setLoading(false);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError(`${doc} file exceeds the 5MB limit`);
        setLoading(false);
        return;
      }
    }
    if (!dataConsent) {
      setError('You must agree to the data protection policy');
      setLoading(false);
      return;
    }

    try {
      const fd = new FormData();
      fd.append('data', JSON.stringify({ ...formData, phone: fullPhone, dataConsent: '1' }));
      fd.append('cv', cv);
      fd.append('coverLetter', coverLetter);
      const docLabels = [];
      for (const doc of requiredDocs) {
        fd.append('documents', documents[doc]);
        docLabels.push(doc);
      }
      fd.append('documentLabels', JSON.stringify(docLabels));

      const res = await axios.post('/api/careers/apply', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const appNumber = res.data?.applicationNumber || '';
      setMessage(`Application submitted successfully! Your application number is ${appNumber}. A confirmation email has been sent.`);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', phoneCode: '+250', positionId: '', educationLevel: '', yearsOfExperience: '' });
      setCv(null);
      setCoverLetter(null);
      setDocuments({});
      setDataConsent(false);
    } catch (err) {
      const data = err.response?.data;
      if (data?.field) setFieldErrors({ [data.field]: data.message });
      if (err.response?.status === 413 || typeof data === 'string') {
        setError('Upload failed: the server rejected your files, usually because they are too large overall. Please compress or reduce your files and try again.');
      } else {
        setError(data?.message || 'Failed to submit application. Please check your internet connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedPosition = positions.find(p => String(p.id) === String(formData.positionId));

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-6 sm:py-12 px-3 sm:px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-6 sm:mb-8">
          <img src="/school.png" alt="Hope Haven Christian School" className="h-24 mx-auto mb-4" />
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-800 mb-1">Hope Haven Christian School</h1>
          <p className="text-lg sm:text-xl font-semibold text-blue-600 mb-1">Career Application Form</p>
          <p className="text-sm sm:text-base text-gray-600">Join our team</p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/careers')}
          className="mb-4 sm:mb-6 text-blue-600 hover:text-blue-800 font-semibold text-sm sm:text-base"
        >
          ← Back to Careers
        </button>

        {/* Important Notice */}
        <div className="mb-4 sm:mb-6 bg-amber-50 border-2 border-amber-400/60 rounded-lg p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.224-.833-2.994 0L3.34 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h3 className="font-bold text-amber-900 text-sm sm:text-base mb-1">Important Notice</h3>
              <p className="text-amber-800 text-xs sm:text-sm leading-relaxed">
                Hope Haven Christian School does <strong>not charge any fee</strong> at any stage of the recruitment process.
                We never ask applicants for money in exchange for job applications, interviews, employment, or any other
                recruitment-related service. Please be cautious of anyone requesting payment while claiming to represent
                Hope Haven Christian School. Only apply through our official recruitment channels.
              </p>
            </div>
          </div>
        </div>

        {message && (
          <div className="mb-4 sm:mb-6 bg-green-50 border-l-4 border-green-600 p-3 sm:p-4 rounded">
            <p className="text-green-800 font-semibold text-sm sm:text-base">{message}</p>
            <button
              onClick={() => navigate('/careers')}
              className="mt-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white font-bold py-2 px-5 rounded-lg text-sm transition duration-200"
            >
              Back to Careers
            </button>
          </div>
        )}
        {error && (
          <div className="mb-4 sm:mb-6 bg-red-50 border-l-4 border-red-600 p-3 sm:p-4 rounded">
            <p className="text-red-800 font-semibold text-sm sm:text-base">{error}</p>
          </div>
        )}
        {verificationMessage && (
          <div className="mb-4 sm:mb-6 bg-green-50 border-l-4 border-green-600 p-3 sm:p-4 rounded">
            <p className="text-green-800 text-sm sm:text-base">{verificationMessage}</p>
          </div>
        )}

        {!message && !emailVerified && (
          <>
            {!codeSent ? (
              <form onSubmit={handleEmailVerification} className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
                <div className="mb-6 sm:mb-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Step 1: Email Verification</h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">First, let's verify your email address. We'll send a verification code to your email.</p>

                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Your Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={emailChecking}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 disabled:bg-gray-100 text-sm sm:text-base"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-600 p-3 sm:p-4 rounded mb-4 sm:mb-6">
                    <p className="text-gray-700 text-xs sm:text-sm">
                      <strong>Important:</strong> You can submit only one application per email address. You'll receive confirmation and updates at this email.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={emailChecking}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-bold py-2 sm:py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {emailChecking ? 'Sending Code...' : 'Send Verification Code'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode} className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
                <div className="mb-6 sm:mb-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Step 1: Enter Verification Code</h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-4">A 6-digit code was sent to <strong>{formData.email}</strong>. Please enter it below.</p>

                  <div className="mb-4">
                    <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Verification Code *</label>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base text-center text-2xl tracking-widest"
                      placeholder="000000"
                      autoFocus
                    />
                  </div>

                  <p className="text-xs text-gray-500 mb-4">The code expires in 10 minutes. Check your spam folder if you don't see it.</p>

                  <button
                    type="submit"
                    disabled={codeVerifying || verificationCode.length !== 6}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-bold py-2 sm:py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {codeVerifying ? 'Verifying...' : 'Verify Code & Continue'}
                  </button>

                  <div className="flex gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => setCodeSent(false)}
                      className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg transition duration-200 text-sm"
                    >
                      Change Email
                    </button>
                    <button
                      type="button"
                      onClick={handleEmailVerification}
                      disabled={emailChecking}
                      className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-800 font-bold py-2 px-4 rounded-lg transition duration-200 text-sm disabled:opacity-50"
                    >
                      Resend Code
                    </button>
                  </div>
                </div>
              </form>
            )}
          </>
        )}

        {!message && emailVerified && (
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
            <div className="mb-4 sm:mb-6 bg-green-50 border-l-4 border-green-600 p-3 sm:p-4 rounded">
              <p className="text-green-800 font-semibold text-sm sm:text-base">✓ Email verified: {formData.email}</p>
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Your Information</h2>

            {selectedPosition && (
              <div className="mb-4 sm:mb-6 bg-blue-50 border border-blue-300 p-3 sm:p-4 rounded">
                <p className="text-blue-800 text-sm sm:text-base">
                  <strong>Applying for:</strong> {selectedPosition.title}
                  {selectedPosition.department && `  •  ${selectedPosition.department}`}
                  {selectedPosition.location && `  •  ${selectedPosition.location}`}
                </p>
                {selectedPosition.deadline && (
                  <p className="text-orange-700 text-sm mt-1 font-semibold">
                    ⏰ Application deadline: {new Date(selectedPosition.deadline).toLocaleString()}
                  </p>
                )}
                {parseDocumentList(selectedPosition.requiredDocuments).length > 0 && (
                  <p className="text-blue-700 text-xs sm:text-sm mt-1">
                    This position requires {parseDocumentList(selectedPosition.requiredDocuments).length} additional document(s). Upload fields will appear below.
                  </p>
                )}
                {selectedPosition.jobAdvertPath && (
                  <a
                    href={`/uploads/${selectedPosition.jobAdvertPath}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-xs sm:text-sm font-semibold text-blue-700 hover:underline"
                  >
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download Job Advert (PDF)
                  </a>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">First Name *</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Last Name *</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Email Address *</label>
                <input type="email" name="email" value={formData.email} disabled className="w-full px-3 sm:px-4 py-2 border-2 bg-gray-100 border-gray-300 rounded-lg text-sm sm:text-base" />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Phone Number *</label>
                <div className="flex">
                  <select name="phoneCode" value={formData.phoneCode} onChange={handlePhoneCodeChange} className="w-1/2 sm:w-[55%] px-2 sm:px-3 py-2 border-2 rounded-l-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base bg-gray-50 border-gray-300 truncate" style={{borderRight: 'none', minWidth: '0'}}>
                    {PHONE_CODES.map(c => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                  <input type="tel" name="phone" value={formData.phone} onChange={handlePhoneChange} required maxLength={15} placeholder="712345678" className={`w-1/2 sm:w-[45%] px-3 sm:px-4 py-2 border-2 rounded-r-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${fieldErrors.phone ? 'border-red-500' : 'border-gray-300'}`} />
                </div>
                <p className="text-gray-500 text-xs mt-1">Select your country code, then enter the number.</p>
                {fieldErrors.phone && <p className="text-red-600 text-xs sm:text-sm mt-1">{fieldErrors.phone}</p>}
              </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Position Applied For *</label>
              {formData.positionId ? (
                <div className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg bg-gray-100 border-gray-300 text-sm sm:text-base text-gray-700 flex items-center justify-between gap-2 ${fieldErrors.positionId ? 'border-red-500' : ''}`}>
                  <span className="flex items-center gap-2 truncate">
                    <svg className="h-4 w-4 text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="truncate">{selectedPosition?.title || `Position #${formData.positionId}`}</span>
                  </span>
                  <span className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5 flex-shrink-0">Locked</span>
                </div>
              ) : (
                <>
                <select name="positionId" value={formData.positionId} onChange={handleChange} required className={`w-full px-3 sm:px-4 py-2 border-2 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base ${fieldErrors.positionId ? 'border-red-500' : 'border-gray-300'}`}>
                  <option value="">Select Position</option>
                  {positions.map(p => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
                {fieldErrors.positionId && <p className="text-red-600 text-xs sm:text-sm mt-1">{fieldErrors.positionId}</p>}
                </>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Highest Education Level *</label>
                <select name="educationLevel" value={formData.educationLevel} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base">
                  <option value="">Select Education Level</option>
                  {EDUCATION_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Years of Experience *</label>
                <select name="yearsOfExperience" value={formData.yearsOfExperience} onChange={handleChange} required className="w-full px-3 sm:px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm sm:text-base">
                  <option value="">Select Experience</option>
                  {['0-1', '1-2', '3-5', '5-10', '10+'].map(opt => (
                    <option key={opt} value={opt}>{opt} years</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Cover Letter (PDF) *</label>
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => {
                  setCoverLetter(e.target.files[0]);
                  setError('');
                }}
                required
                className="w-full text-sm sm:text-base file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {coverLetter && <p className="text-green-600 text-xs mt-1">✓ {coverLetter.name}</p>}
              <p className="text-gray-500 text-xs mt-1">PDF only — maximum file size: 5MB</p>
            </div>

              <div className="mb-4 sm:mb-6">
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">CV / Resume (PDF, DOC, DOCX) *</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    setCv(e.target.files[0]);
                    setError('');
                  }}
                  required
                  className="w-full text-sm sm:text-base file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {cv && <p className="text-green-600 text-xs mt-1">✓ {cv.name}</p>}
                <p className="text-gray-500 text-xs mt-1">Maximum file size: 5MB</p>
              </div>

            {selectedPosition && parseDocumentList(selectedPosition.requiredDocuments).length > 0 && (
              <div className="mb-4 sm:mb-6">
                <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">Additional Required Documents</label>
                <div className="space-y-4">
                  {parseDocumentList(selectedPosition.requiredDocuments).map(doc => (
                    <div key={doc} className="bg-gray-50 border border-gray-300 rounded-lg p-3 sm:p-4">
                      <label className="block text-gray-700 font-semibold mb-2 text-sm sm:text-base">{doc} *</label>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => handleDocChange(doc, e.target.files[0])}
                        className="w-full text-sm sm:text-base file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      {documents[doc] && <p className="text-green-600 text-xs mt-1">✓ {documents[doc].name}</p>}
                      <p className="text-gray-500 text-xs mt-1">PDF, DOC, DOCX, JPG or PNG — maximum file size: 5MB</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4 sm:mb-6 bg-gray-50 border border-gray-300 p-3 sm:p-4 rounded">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={dataConsent} onChange={(e) => setDataConsent(e.target.checked)} className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="text-sm sm:text-base text-gray-700">
                  I consent to the collection and processing of my personal data in accordance with the data protection policy of Hope Haven Christian School. I understand that my information will be used solely for recruitment and employment purposes.
                </span>
              </label>
            </div>

            <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8">
              <button
                type="button"
                onClick={() => navigate('/careers')}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white font-bold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CareerApply;
